import type { IMapLayoutSpotSurface } from '@interfaces/map';

import {
  MODULE_DIRECTION_STRAIGHT,
  type MapEditorCatalogElement,
  type MapEditorFootprint,
} from './mapEditorCatalog';

// Small plan drawings for the picker: one module as a box with the walk through it, and a whole
// chain as the boxes it would stand in.
//
// Drawings, not the plan. The real overhead plan is Unreal's — it is what the author is looking at
// in the stream behind this panel, rendered from the geometry that is actually standing. These are
// the thumbnails on a picker card, where the question is only "which of these is the one that turns
// left" and where nothing is standing to render. Two consequences: the walk here is a straight-line
// approximation of a module's sweep, and a chain here advances by a module's length and pivots on
// its direction rather than following an authored exit. Both are wrong by a few centimetres and
// right about the shape, which is all a 48px glyph can carry.
//
// Every number is a proportion of the drawing's own span, so one glyph reads the same at 36px on a
// starter card and at 96px in the carousel, and an SVG scaled by the layout keeps its line weights.

// What a module with no measured footprint is drawn as: a square, with the walk straight through it.
// A shape rather than a gap, because a module the build could not measure is still pickable and its
// card still needs a picture — and a square says "a module, size unknown" more usefully than an
// empty frame says anything.
const NOMINAL_FOOTPRINT: MapEditorFootprint = {
  lengthCm: 1000,
  widthCm: 1000,
};

export interface MapEditorGlyph {
  viewBox: string;
  box: { x: number; y: number; width: number; height: number };
  // Polyline points for the walk through the module.
  walk: string;
  strokeWidth: number;
  walkStrokeWidth: number;
  radius: number;
}

/**
 * One module as a plan glyph: its footprint, with the walk entering on the left and leaving the way
 * its direction says.
 *
 * The walk is what makes the four corridor lengths tell each other apart at a glance, and it is the
 * whole reason these glyphs exist rather than a coloured rectangle: `Corridor Short Left` and
 * `Corridor Short Right` are the same box.
 *
 * @param footprint - The module's measured size, or null to draw the nominal square.
 * @param direction - Which way the walk leaves: `left`, `right`, or straight through.
 * @returns Everything an SVG needs, in the drawing's own units.
 */
export const moduleGlyph = (
  footprint: MapEditorFootprint | null,
  direction: string,
): MapEditorGlyph => {
  const { lengthCm, widthCm } = footprint ?? NOMINAL_FOOTPRINT;
  const span = Math.max(lengthCm, widthCm);
  const pad = span * 0.1;

  // Where the walk turns: past the middle rather than at it, so a turning corridor reads as a
  // corner rather than as a T.
  const turnAt = lengthCm * 0.55;
  const middle = widthCm / 2;

  const legs = [`0,${middle}`, `${turnAt},${middle}`];
  if (direction === 'left') {
    // Screen coordinates, so the walker's left is up the page: -Y in the module's own frame is the
    // way a left-turning module sweeps, and the plan is drawn looking down on it.
    legs.push(`${turnAt},${widthCm * 0.14}`);
  } else if (direction === 'right') {
    legs.push(`${turnAt},${widthCm * 0.86}`);
  } else {
    legs.push(`${lengthCm},${middle}`);
  }

  return {
    viewBox: `${-pad} ${-pad} ${lengthCm + pad * 2} ${widthCm + pad * 2}`,
    box: { x: 0, y: 0, width: lengthCm, height: widthCm },
    walk: legs.join(' '),
    strokeWidth: span * 0.014,
    walkStrokeWidth: span * 0.017,
    radius: span * 0.02,
  };
};

export interface MapEditorChainGlyph {
  viewBox: string;
  boxes: { x: number; y: number; width: number; height: number }[];
  strokeWidth: number;
}

/**
 * A whole chain as a plan glyph: one box per module, laid out end to end and pivoting where a module
 * turns.
 *
 * What a starter layout's card shows. The author is choosing between "turns back on itself" and "one
 * straight walk", and that is a shape rather than a list of room names.
 *
 * @param elements - The chain, in order. An unmeasured module contributes the nominal square, so one
 * gap does not collapse the whole drawing.
 * @returns The boxes and a viewBox framing them, or null for an empty chain — which has no shape and
 * whose caller should draw nothing rather than an empty frame.
 */
export const chainGlyph = (
  elements: MapEditorCatalogElement[],
): MapEditorChainGlyph | null => {
  if (!elements.length) {
    return null;
  }

  // Heading, as a unit vector on the plan. Starts along +X, which is the frame every module's
  // footprint is measured in.
  let headingX = 1;
  let headingY = 0;
  let cursorX = 0;
  let cursorY = 0;

  const boxes: MapEditorChainGlyph['boxes'] = [];
  let minX = 0;
  let minY = 0;
  let maxX = 0;
  let maxY = 0;

  elements.forEach((element) => {
    const { lengthCm, widthCm } = element.footprint ?? NOMINAL_FOOTPRINT;

    // The box is the module's own length × width, turned by whichever axis the walk is running
    // along. Centred across the walk and starting at the cursor along it, which is where its entry
    // doorway sits.
    const alongX = headingX !== 0;
    const x = alongX
      ? cursorX + (headingX > 0 ? 0 : -lengthCm)
      : cursorX - widthCm / 2;
    const y = alongX
      ? cursorY - widthCm / 2
      : cursorY + (headingY > 0 ? 0 : -lengthCm);
    const width = alongX ? lengthCm : widthCm;
    const height = alongX ? widthCm : lengthCm;

    boxes.push({ x, y, width, height });

    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + width);
    maxY = Math.max(maxY, y + height);

    cursorX += headingX * lengthCm;
    cursorY += headingY * lengthCm;

    // The pivot. A left turn takes the heading anticlockwise on the plan, a right turn clockwise;
    // straight leaves it alone, which is what every room but a rotunda does.
    if (element.direction === 'left') {
      const previous = headingX;
      headingX = headingY;
      headingY = -previous;
    } else if (element.direction === 'right') {
      const previous = headingX;
      headingX = -headingY;
      headingY = previous;
    }
  });

  const width = maxX - minX;
  const height = maxY - minY;
  const span = Math.max(width, height, 1);
  const pad = span * 0.08;

  return {
    viewBox: `${minX - pad} ${minY - pad} ${width + pad * 2} ${
      height + pad * 2
    }`,
    boxes,
    strokeWidth: span * 0.012,
  };
};

// The drawn interior, in its own 400 × 250 frame. A one-point perspective box seen from the entry
// doorway, with a panel on each side wall for each place this module has for a picture.
//
// A stand-in, and the design says so on the frame. Renders now exist — `MytaExportLayoutThumbnails`
// copies each module level's own cached editor thumbnail — but only for the levels an artist has
// saved from a viewport, so a kit is normally part rendered and part not. This is what the rest fall
// back to, and it is not a placeholder rectangle, because the count of panels down the walls is the
// one thing an author is actually comparing between a gallery and a screen gallery.
const INTERIOR_WIDTH = 400;
const INTERIOR_HEIGHT = 250;
// Where the walls converge. Slightly above centre, which is where a standing eye puts it.
const VANISHING_X = 200;
const VANISHING_Y = 122;
// How far toward the vanishing point the back wall sits. Short of it, so the box has a back rather
// than closing to a point.
const DEPTH = 0.9;
// What a frame with no measured size is drawn at — the ratio every frame used before `surfaces`
// existed, so an unmeasured module looks exactly as it did rather than newly wrong.
const NOMINAL_PANEL_ASPECT = 1.5;
// How much of the wall's depth one panel spans, and how tall that makes it. Two constants rather
// than one number so the aspect can be honoured without the run's spacing depending on it: the
// spacing answers "how many frames", the height answers "what shape".
const PANEL_WIDTH_ON_WALL = 0.34;
const PANEL_HEIGHT_SCALE = 0.62;

export interface MapEditorInterior {
  floor: string;
  ceiling: string;
  wallLeft: string;
  wallRight: string;
  // Perspective guides down the side walls, and the two long edges of the box.
  lines: { x1: number; y1: number; x2: number; y2: number }[];
  // One quad per place for a picture, split between the two side walls.
  panels: string[];
  viewBox: string;
}

/**
 * Draws the inside of a module: the box, and a frame on the walls for every picture it holds.
 *
 * Each frame is drawn at its own measured aspect where the build supplies one. That is the whole
 * reason `surfaces` is threaded down here: the shipped kit holds eleven distinct shapes, from an
 * exact square to an 11:1 banner, and a screen gallery drawn at a picture gallery's ratio is the one
 * thing this drawing exists to tell apart. A surface the build could not measure falls back to the
 * nominal ratio, which is what every frame used before the field existed.
 *
 * @param spotCount - How many places for pictures the build measured. Zero draws the bare box, which
 * is the honest picture of a module with nothing on its walls.
 * @param surfaces - Their measured sizes, in the order the build reports them. Shorter than
 * `spotCount`, or absent, is normal: the count and the shapes come from different fields and a build
 * may answer one and not the other.
 * @returns Polygon point strings and guide lines in the drawing's own frame.
 */
export const interiorDrawing = (
  spotCount: number,
  surfaces: IMapLayoutSpotSurface[] = [],
): MapEditorInterior => {
  // A point on the left or right wall, `t` of the way from the viewer to the back.
  const acrossAt = (edge: number, t: number) => edge + (VANISHING_X - edge) * t;
  const topAt = (t: number) => VANISHING_Y * t;
  const bottomAt = (t: number) =>
    INTERIOR_HEIGHT + (VANISHING_Y - INTERIOR_HEIGHT) * t;

  const lines: MapEditorInterior['lines'] = [];
  for (let step = 1; step <= 4; step += 1) {
    const t = step / 5;
    [0, INTERIOR_WIDTH].forEach((edge) => {
      lines.push({
        x1: acrossAt(edge, t),
        y1: topAt(t),
        x2: acrossAt(edge, t),
        y2: bottomAt(t),
      });
    });
  }
  [0, INTERIOR_WIDTH].forEach((edge) => {
    lines.push({
      x1: edge,
      y1: bottomAt(0),
      x2: acrossAt(edge, DEPTH),
      y2: bottomAt(DEPTH),
    });
    lines.push({
      x1: edge,
      y1: topAt(0),
      x2: acrossAt(edge, DEPTH),
      y2: topAt(DEPTH),
    });
  });

  // Split between the walls the way a gallery hangs them: the odd one goes on the left, which is the
  // wall the eye reaches first.
  const perWall = [Math.ceil(spotCount / 2), Math.floor(spotCount / 2)];
  const panels: string[] = [];
  // Which surface a panel is drawing. The split above is this drawing's own convention rather than
  // the kit's hanging order, so the pairing is by running index: panel n gets surface n. It is a
  // picture of the shapes a module holds, not a claim about which wall each one is on.
  let surfaceIndex = 0;
  [0, INTERIOR_WIDTH].forEach((edge, wall) => {
    const count = perWall[wall];
    for (let index = 0; index < count; index += 1) {
      const measured = surfaces[surfaceIndex];
      surfaceIndex += 1;
      // Both numbers or neither: a surface with one of them is not a shape, and dividing by an
      // absent height would make every such frame a hairline.
      const aspect =
        measured?.widthCm && measured?.heightCm
          ? measured.widthCm / measured.heightCm
          : NOMINAL_PANEL_ASPECT;
      // The run of panels occupies the middle three quarters of the wall's depth, so the nearest one
      // is not cut off by the frame and the furthest is not lost in the corner.
      const step = 0.74 / Math.max(count, 1);
      const near = 0.08 + index * step + step * 0.1;
      const far = near + step * 0.76;
      // The frame's height as a fraction of the wall, from its own aspect. Anchored on the width the
      // run already allots each panel, so a wide banner stays short and a square stands tall in the
      // same slot rather than the slots changing size — the run's spacing is about how many frames
      // there are, not what shape they are.
      const half = Math.min(
        0.42,
        (PANEL_WIDTH_ON_WALL / aspect) * PANEL_HEIGHT_SCALE,
      );
      const height = (t: number, fraction: number) =>
        topAt(t) + (bottomAt(t) - topAt(t)) * fraction;

      panels.push(
        [
          `${acrossAt(edge, near)},${height(near, 0.5 - half)}`,
          `${acrossAt(edge, far)},${height(far, 0.5 - half)}`,
          `${acrossAt(edge, far)},${height(far, 0.5 + half)}`,
          `${acrossAt(edge, near)},${height(near, 0.5 + half)}`,
        ].join(' '),
      );
    }
  });

  return {
    viewBox: `0 0 ${INTERIOR_WIDTH} ${INTERIOR_HEIGHT}`,
    floor: [
      `0,${INTERIOR_HEIGHT}`,
      `${INTERIOR_WIDTH},${INTERIOR_HEIGHT}`,
      `${acrossAt(INTERIOR_WIDTH, DEPTH)},${bottomAt(DEPTH)}`,
      `${acrossAt(0, DEPTH)},${bottomAt(DEPTH)}`,
    ].join(' '),
    ceiling: [
      '0,0',
      `${INTERIOR_WIDTH},0`,
      `${acrossAt(INTERIOR_WIDTH, DEPTH)},${topAt(DEPTH)}`,
      `${acrossAt(0, DEPTH)},${topAt(DEPTH)}`,
    ].join(' '),
    // Both side walls, so a finish put on `wall` is something the author can actually see change.
    wallLeft: [
      '0,0',
      `${acrossAt(0, DEPTH)},${topAt(DEPTH)}`,
      `${acrossAt(0, DEPTH)},${bottomAt(DEPTH)}`,
      `0,${INTERIOR_HEIGHT}`,
    ].join(' '),
    wallRight: [
      `${INTERIOR_WIDTH},0`,
      `${acrossAt(INTERIOR_WIDTH, DEPTH)},${topAt(DEPTH)}`,
      `${acrossAt(INTERIOR_WIDTH, DEPTH)},${bottomAt(DEPTH)}`,
      `${INTERIOR_WIDTH},${INTERIOR_HEIGHT}`,
    ].join(' '),
    lines,
    panels,
  };
};

/**
 * How long a module is, in whole metres.
 *
 * The one measurement worth putting in words next to the glyph: an author choosing between three
 * corridors is choosing a length, and "26 m" answers that where `Long` only ranks it.
 *
 * @param element - The module.
 * @returns The rounded length, or null when the build measured none — which the caller says nothing
 * about rather than reporting as zero.
 */
export const moduleLengthMetres = (
  element: MapEditorCatalogElement,
): number | null =>
  element.footprint ? Math.round(element.footprint.lengthCm / 100) : null;

/**
 * Whether a module turns the walk.
 *
 * @param element - The module.
 * @returns True for anything but a straight run.
 */
export const turnsTheWalk = (element: MapEditorCatalogElement): boolean =>
  element.direction !== MODULE_DIRECTION_STRAIGHT;
